<template>
  <div>
    <agent-purchase-item-cart-money-info
      :fee-info="feeInfo"
      :product-fee="productFee"
      :shipping-fee="shippingFee"
      :order-fee="orderFee"
      :loading="loading"
      :is-simiot-contracting-party="isSimiotContractingParty"
    />

    <div class="iotplt-content-box item-list">
      <div class="iotplt-table-operation-buttons">
        <a-space>
          <a-popover trigger="hover" placement="left">
            <template slot="content">
              <p>*可用额度=账户余额+信用额度-锁定金额</p>
              <p>*订单金额=产品金额+预计快递费（按商家邮寄口径；结算选自提则不收快递费）</p>
              <p>*账户可用额度要大于等于订单总额，才能进入结算</p>
              <p>*订单提交后，平台会在一个工作日内进行审核，客服人员可能会与您确认订单信息</p>
            </template>
            <a-icon type="question-circle" />
          </a-popover>
          <a-button :selected-row-keys="selectedRowKeys" type="primary" @click="toSettlement">结算</a-button>
          <destroy-agent-purchase-item :selected-row-keys="selectedRowKeys" @completed="fetchData" />
        </a-space>
      </div>

      <a-table
        size="middle"
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :scroll="{ x: 'max-content' }"
        :pagination="false"
        :row-selection="{ selectedRowKeys: selectedRowKeys,
                          onChange: onSelectChange }"
        :custom-row="customItemRow"
        row-key="id"
      >
        <div
          slot="product_name"
          slot-scope="product_name, record"
        >
          <span v-if="record.valid">
            {{ product_name }}
          </span>
          <a-space v-else>
            <a-tooltip>
              <template slot="title">
                该产品不可采购
              </template>
              <a-icon type="warning" style="color: #ec5c5c" />
            </a-tooltip>
            {{ product_name }}
          </a-space>
        </div>

        <div
          slot="service_period"
          slot-scope="service_period, record, index"
        >
          <template v-if="isCountOnlyProduct(record)">--</template>
          <a-spin :spinning="record.loading === true" v-else>
            <a-input-number
              :min="1"
              :max="servicePeriodMaxValue"
              :step="1"
              :precision="0"
              style="width: 120px"
              :disabled="!record.valid"
              :formatter="parseServicePeriodInputValue"
              :parser="parseServicePeriodInputValue"
              @change="(servicePeriod) => handleServicePeriodChange(servicePeriod, record, index)"
              v-model="record.service_period"
            />
          </a-spin>
        </div>

        <div
          slot="product_count"
          slot-scope="product_count, record, index"
        >
          <a-spin :spinning="record.loading === true">
            <a-input-number
              :min="1"
              :max="productCountMaxValue"
              :step="1"
              :precision="0"
              style="width: 120px"
              :disabled="!record.valid"
              :formatter="parseProductCountInputValue"
              :parser="parseProductCountInputValue"
              @change="(productCount) => handleProductCountChange(productCount, record, index)"
              v-model="record.product_count"
            />
          </a-spin>
        </div>
      </a-table>

      <pagination
        v-show="pagination.total_count > 0"
        :total-count="pagination.total_count"
        :page.sync="query.page"
        :per-page.sync="query.per_page"
        @change="fetchData"
      />

    </div>
  </div>
</template>

<script>
import AgentPurchaseItemCartMoneyInfo from '@/views/agent_purchase_items/cart/MoneyInfo'
import Pagination from '@/components/Pagination'
import { findCartInfo, updateAgentPurchaseItemFee } from '@/api/agent_purchase_item'
import DestroyAgentPurchaseItem from '@/views/agent_purchase_items/cart/Destroy'
import { formatFloat } from '@/utils/filter'
import { numberRoundTwo } from '@/utils/math'

export default {
  name: 'AgentPurchaseItemCart',
  components: {
    Pagination,
    AgentPurchaseItemCartMoneyInfo,
    DestroyAgentPurchaseItem
  },
  data() {
    this.calculateProductFee = this.$lodash.debounce(this.calculateProductFee, 400)
    return {
      data: [],
      items: [],
      servicePeriodMaxValue: 10000000,
      productCountMaxValue: 10000000,
      feeInfo: {},
      loading: true,
      selectedRowKeys: [],
      productFee: 0,
      shippingFee: 0,
      orderFee: 0,
      shippingFeeMismatch: false,
      pagination: {
        total_count: 0
      },
      query: {},
      isSimiotContractingParty: false
    }
  },
  computed: {
    columns() {
      return [
        {
          title: '产品类型',
          dataIndex: 'product_type',
          fixed: 'left'
        },
        {
          title: '产品名称',
          dataIndex: 'product_name',
          scopedSlots: { customRender: 'product_name' },
          fixed: 'left'
        },
        {
          title: '运营商种类',
          dataIndex: 'carrier_type'
        },
        {
          title: '用户业务模式',
          dataIndex: 'user_business_type'
        },
        {
          title: '套餐周期',
          dataIndex: 'service_cycle'
        },
        {
          title: '套餐容量',
          dataIndex: 'package_capacity'
        },
        {
          title: '语音',
          dataIndex: 'voice_capacity'
        },
        {
          title: '卡品',
          dataIndex: 'card_type'
        },
        {
          title: '服务周期数(个)',
          dataIndex: 'service_period',
          scopedSlots: { customRender: 'service_period' }
        },
        {
          title: '采购数量',
          dataIndex: 'product_count',
          scopedSlots: { customRender: 'product_count' }
        },
        {
          title: '单价(元)',
          dataIndex: 'distributor_price',
          customRender: formatFloat
        },
        {
          title: '卡板价格',
          dataIndex: 'card_type_price'
        },
        {
          title: '产品费用(元)',
          dataIndex: 'product_fee',
          customRender: formatFloat
        },
        {
          title: '卡板费用(元)',
          dataIndex: 'card_fee',
          customRender: formatFloat
        },
        {
          title: '小计(元)',
          dataIndex: 'total_fee',
          customRender: formatFloat
        },
        {
          title: '备注',
          dataIndex: 'remark',
          width: 150,
          ellipsis: true
        }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
      this.calculateOrderFee()
    },

    // 自定义项目行样式
    customItemRow(record) {
      return {
        style: {
          color: record.valid ? '' : '#c7c7c7' // 无效的项目字体颜色
        }
      }
    },

    isCountOnlyProduct(record) {
      return record.product_type_slug === 'comm_module' ||
        record.product_type_slug === 'cpe' ||
        record.product_type_slug === 'esim' ||
        record.product_type_slug === 'profile'
    },

    calculateProductFee(record, index) {
      this.items[index].loading = true
      this.$set(this.items, index, this.items[index])

      const countOnly = this.isCountOnlyProduct(record)
      const productCount = record.product_count
      if ((!countOnly && !(record.service_period && productCount)) || (countOnly && !productCount)) {
        this.items[index].loading = false
        this.$set(this.items, index, this.items[index])
        return
      }

      const data = countOnly
        ? {
          agent_id: this.$store.getters.userAgentId,
          product_count: productCount
        }
        : {
          agent_id: this.$store.getters.userAgentId,
          service_period: record.service_period,
          product_count: productCount
        }

      updateAgentPurchaseItemFee(record.id, data).then((res) => {
        if (res.code === 0) {
          this.$lodash.set(this.items, `[${index}].product_fee`, res.data.product_fee)
          this.$lodash.set(this.items, `[${index}].card_fee`, res.data.card_fee)
          this.$lodash.set(this.items, `[${index}].total_fee`, res.data.total_fee)
          this.$lodash.set(this.items, `[${index}].distributor_price`, res.data.distributor_price)
          this.$lodash.set(this.items, `[${index}].card_type_price`, res.data.card_type_price)
          this.$lodash.set(this.items, `[${index}].product_count`, productCount)

          this.items[index].loading = false
          this.$set(this.items, index, this.items[index])

          if (this.selectedRowKeys.includes(record.id)) {
            this.calculateOrderFee()
          }
        } else {
          this.items[index].loading = false
          this.$set(this.items, index, this.items[index])
        }
      })
    },

    calculateOrderFee() {
      let productFee = 0
      const preOrderFees = {}
      this.selectedRowKeys.forEach((id) => {
        const item = this.$lodash.find(this.items, { id: id })
        if (!item) {
          return
        }
        productFee += numberRoundTwo(item.total_fee)
        if (item.agent_pre_order_id) {
          preOrderFees[item.agent_pre_order_id] = numberRoundTwo(item.shipping_fee || 0)
        }
      })

      const uniqueFees = this.$lodash.uniq(Object.values(preOrderFees))
      let shippingFee = 0
      let shippingFeeMismatch = false
      if (uniqueFees.length === 1) {
        shippingFee = uniqueFees[0]
      } else if (uniqueFees.length > 1) {
        shippingFeeMismatch = true
        shippingFee = Math.max.apply(null, uniqueFees)
      }

      this.productFee = numberRoundTwo(productFee)
      this.shippingFee = numberRoundTwo(shippingFee)
      this.shippingFeeMismatch = shippingFeeMismatch
      this.orderFee = numberRoundTwo(this.productFee + this.shippingFee)
    },

    parseServicePeriodInputValue(val) {
      val ||= 1
      if (val < 1) {
        val = 1
      } else if (val > this.servicePeriodMaxValue) {
        val = this.servicePeriodMaxValue
      }
      return parseInt(val)
    },

    parseProductCountInputValue(val) {
      val ||= 1
      if (val < 1) {
        val = 1
      } else if (val > this.productCountMaxValue) {
        val = this.productCountMaxValue
      }
      return parseInt(val)
    },

    handleServicePeriodChange(servicePeriod, record, index) {
      this.calculateProductFee(record, index)
    },

    handleProductCountChange(productCount, record, index) {
      record.product_count = productCount
      this.calculateProductFee(record, index)
    },

    isInvalidItemSelected() {
      const invalidIds = this.$lodash.map(this.items.filter(item => !item.valid), 'id')
      return this.selectedRowKeys.some(function(key) {
        if (invalidIds.includes(key)) {
          return true
        }
      })
    },

    isMixedProductTypeSelected() {
      const selected = this.items.filter((item) => this.selectedRowKeys.includes(item.id))
      const types = this.$lodash.uniq(selected.map((item) => item.product_type_slug || 'sim_card'))
      return types.length > 1
    },

    toSettlement() {
      if (this.selectedRowKeys.length === 0) {
        this.$warning({
          title: '请选择采购项',
          content: ''
        })
      } else if (this.isInvalidItemSelected()) {
        this.$warning({
          title: '请勿勾选无效的项目',
          content: ''
        })
      } else if (this.isMixedProductTypeSelected()) {
        this.$warning({
          title: '同一订单只能结算同一种产品类型',
          content: '请将物联网卡与模组分开结算'
        })
      } else if (this.shippingFeeMismatch) {
        this.$warning({
          title: '购物车存在不同快递费用，请分别结算',
          content: ''
        })
      } else if (this.feeInfo.available < this.orderFee) {
        this.$warning({
          title: '可用额度小于订单金额，无法进入结算',
          content: '订单金额已含预计快递费（按商家邮寄口径）'
        })
      } else {
        this.$router.push({ name: 'cart_settlement', query: { ids: this.selectedRowKeys.join(',') }})
      }
    },

    fetchData() {
      this.loading = true
      this.selectedRowKeys = []
      this.productFee = 0
      this.shippingFee = 0
      this.orderFee = 0
      this.shippingFeeMismatch = false
      findCartInfo(
        Object.assign({ agent_id: this.$store.getters.userAgentId }, this.query)
      ).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.items = res.data.agent_purchase_items || []
          this.feeInfo = res.data.fee_info
          this.isSimiotContractingParty = res.data.is_simiot_contracting_party
          this.pagination = res.pagination
        }
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.item-list {
  margin: 20px 15px 0 0;
}

</style>
